/* Pathway Coffee cart interactions
   File: assets/pathway-cart.js
*/
(() => {
  const roots = new WeakSet();

  function initCart(root) {
    if (!root || roots.has(root)) return;
    roots.add(root);

    const form = root.querySelector('[data-pc-cart-form]');
    if (!form) return;

    let activeRequest = null;

    /*
     * Nonprofit partner code -> cart attribute "Partner code".
     * Attribution only (no discount). /cart/update.js with just
     * { attributes: { 'Partner code': value } } merges into the existing
     * cart attributes; it does not touch the order note, other attributes
     * or line items. An empty value removes the attribute.
     */
    const PARTNER_ATTRIBUTE = 'Partner code';
    const partnerInput = root.querySelector('[data-pc-partner-input]');
    const partnerStatus = root.querySelector('[data-pc-partner-status]');
    let partnerSaved = partnerInput ? normalizePartnerCode(partnerInput.value) : '';
    let partnerPending = Promise.resolve();
    let partnerRequested = partnerSaved;

    function normalizePartnerCode(value) {
      return String(value || '').trim().replace(/\s+/g, ' ').toUpperCase();
    }

    function setPartnerStatus(text, isError) {
      if (!partnerStatus) return;
      partnerStatus.textContent = text;
      partnerStatus.classList.toggle('is-error', Boolean(isError));
    }

    function savePartnerCode() {
      if (!partnerInput) return partnerPending;
      const value = normalizePartnerCode(partnerInput.value);
      partnerInput.value = value;
      if (value === partnerRequested) return partnerPending;
      partnerRequested = value;

      partnerPending = fetch(`${cartRoot()}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ attributes: { [PARTNER_ATTRIBUTE]: value } })
      })
        .then((response) => {
          if (!response.ok) throw new Error('save failed');
          partnerSaved = value;
          setPartnerStatus(value ? 'Partner code saved.' : '', false);
        })
        .catch(() => {
          // Allow a retry; the field also posts with checkout, so nothing is lost.
          partnerRequested = partnerSaved;
          setPartnerStatus('We couldn\u2019t save the code yet. It will be included when you check out.', true);
        });
      return partnerPending;
    }

    if (partnerInput && partnerInput.dataset.pcPartnerReady !== 'true') {
      partnerInput.dataset.pcPartnerReady = 'true';
      partnerInput.addEventListener('change', savePartnerCode);
      partnerInput.addEventListener('keydown', (event) => {
        // Enter saves the code instead of submitting the form to checkout.
        if (event.key === 'Enter') {
          event.preventDefault();
          savePartnerCode();
        }
      });
      // Normalize what the native checkout/update submit sends.
      form.addEventListener('submit', () => {
        partnerInput.value = normalizePartnerCode(partnerInput.value);
      });
    }

    function cartRoot() {
      return window.Shopify?.routes?.root || '/';
    }

    /*
     * Recompute this line's inventory-aware internal properties for the new
     * quantity, using the exact _same_ getInventoryStatus() function the
     * product page and collection cards use, so a quantity change in the
     * cart can never disagree with how they'd have classified the same
     * (variant, quantity) pair.
     *
     * Returns null - meaning "do not touch properties, let /cart/change.js
     * preserve them natively by omitting the properties key" - whenever
     * anything needed to do this safely and correctly is unavailable:
     *   - the shared PathwayAppstle engine did not load,
     *   - the current cart (fetched fresh, not read from stale DOM state)
     *     does not contain this exact line,
     *   - this is a Prelaunch line, where inventory must NEVER change the
     *     recorded fulfillment status/dates (those stay exactly as
     *     submitted at add-to-cart time; Prelaunch is authoritative).
     *
     * This is a deliberate safety choice: recomputing is only ever
     * attempted when it can be done from real, freshly-fetched data. There
     * is no path where we submit a guessed or partial properties object -
     * omitting `properties` entirely (Shopify's own default: preserve
     * as-is) is always the fallback, never a destructive replacement.
     */
    async function buildUpdatedProperties(lineElement, newQuantity) {
      const api = window.PathwayAppstle;
      if (!api || typeof api.getInventoryStatus !== 'function') return null;

      const lineKey = lineElement.dataset.lineKey;
      if (!lineKey) return null;

      // Prelaunch lines: inventory must never change what was recorded.
      // Leave every property exactly as-is by not sending `properties`.
      if (lineElement.dataset.isPrelaunchLine === 'true') return null;

      let cart;
      try {
        const cartResponse = await fetch(`${cartRoot()}cart.js`, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin'
        });
        if (!cartResponse.ok) return null;
        cart = await cartResponse.json();
      } catch (error) {
        return null;
      }

      const items = Array.isArray(cart?.items) ? cart.items : [];
      const currentItem = items.find((item) => item.key === lineKey);
      if (!currentItem || !currentItem.properties) return null;

      const variantId = lineElement.dataset.variantId;
      const existingCartQuantity = items.reduce((sum, item) => {
        if (item.key === lineKey) return sum; // exclude this line itself
        if (String(item.variant_id) !== String(variantId)) return sum;
        return sum + api.normalizeNonNegativeInt(item.quantity);
      }, 0);

      const managementRaw = lineElement.dataset.inventoryManagement || '';
      const rawQuantity = lineElement.dataset.inventoryQuantity;
      const variantLike = {
        inventoryManagement: managementRaw || null,
        // An empty data attribute means Shopify returned no value (blank
        // Liquid output), which must be treated as "no number", not 0.
        inventoryQuantity: rawQuantity === '' || rawQuantity == null ? null : Number(rawQuantity)
      };

      const result = api.getInventoryStatus(variantLike, newQuantity, existingCartQuantity);

      let statusValue;
      let reviewRequiredValue;
      if (result.status === 'sufficient') {
        statusValue = 'available-inventory';
        reviewRequiredValue = false;
      } else if (result.status === 'insufficient') {
        statusValue = 'next-roast-review';
        reviewRequiredValue = true;
      } else {
        statusValue = 'inventory-unknown-review';
        reviewRequiredValue = true;
      }

      /*
       * Start from the CURRENT properties Shopify just returned for this
       * line (not from any DOM-reconstructed guess), so every existing
       * property - Appstle's, Fresh Roast's, Prelaunch's, or a customer's
       * own note - survives untouched except the specific inventory keys
       * below. _Estimated Next Billing Date / _Estimated Recurring
       * Shipment are deliberately left alone: they depend on which billing
       * cycle this line is in and today's date, not on quantity, so a
       * quantity change never changes them.
       */
      const updatedProperties = Object.assign({}, currentItem.properties);
      updatedProperties['_Initial Requested Quantity'] = String(result.requestedQuantity);
      updatedProperties['_Initial Existing Cart Quantity'] = String(result.existingCartQuantity);
      updatedProperties['_Initial Inventory Tracked'] = result.tracked ? 'true' : 'false';
      updatedProperties['_Initial Inventory Quantity'] =
        result.inventoryQuantity === null ? '' : String(result.inventoryQuantity);
      updatedProperties['_Initial Fulfillment Status'] = statusValue;
      updatedProperties['_Initial Fulfillment Review Required'] = reviewRequiredValue ? 'true' : 'false';

      /*
       * Keep the customer-visible checkout copies of the inventory status
       * (Ships / Schedule Details, set at add-to-cart time) in step with the
       * new quantity, so checkout never shows an "In stock" line the cart no
       * longer agrees with. Only rewritten when the line already carries
       * them and the matching Theme-settings text was rendered onto the line.
       */
      const sufficientNow = result.status === 'sufficient';
      const has = (key) => Object.prototype.hasOwnProperty.call(currentItem.properties, key);
      const detailsText = sufficientNow
        ? lineElement.dataset.pcDetailsAvailable
        : lineElement.dataset.pcDetailsInsufficient;
      const shipsText = sufficientNow
        ? lineElement.dataset.pcShipsAvailable
        : lineElement.dataset.pcShipsInsufficient;
      if (has('Schedule Details') && detailsText) updatedProperties['Schedule Details'] = detailsText;
      if (has('Ships') && shipsText) updatedProperties['Ships'] = shipsText;

      return updatedProperties;
    }

    async function changeLine(lineElement, quantity, messageElement) {
      if (activeRequest) activeRequest.abort();
      activeRequest = new AbortController();

      root.classList.add('is-loading');
      if (messageElement) messageElement.textContent = '';
      savePartnerCode();

      const lineKey = lineElement.dataset.lineKey;

      try {
        // Best-effort: only ever changes what is submitted when it can be
        // computed from verified, freshly-fetched data (see
        // buildUpdatedProperties above). A failure here still lets the
        // quantity change proceed - just without recomputed properties -
        // rather than blocking the customer's cart update.
        const updatedProperties = quantity > 0
          ? await buildUpdatedProperties(lineElement, quantity)
          : null;

        const body = { quantity };
        // Prefer the line's stable key over its numeric position, which
        // shifts if another line is removed concurrently.
        if (lineKey) {
          body.id = lineKey;
        } else {
          body.line = Number(lineElement.dataset.lineIndex);
        }
        if (updatedProperties) {
          body.properties = updatedProperties;
        }

        const response = await fetch(`${cartRoot()}cart/change.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(body),
          signal: activeRequest.signal
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.description || data.message || 'The cart could not be updated.');
        }

        await partnerPending;
        window.location.reload();
      } catch (error) {
        if (error.name === 'AbortError') return;
        root.classList.remove('is-loading');
        if (messageElement) {
          messageElement.textContent = error.message || 'The cart could not be updated.';
        }
      }
    }

    root.querySelectorAll('[data-pc-cart-line]').forEach((lineElement) => {
      const input = lineElement.querySelector('[data-pc-quantity-input]');
      const message = lineElement.querySelector('[data-pc-line-message]');

      lineElement.querySelectorAll('[data-pc-quantity-change]').forEach((button) => {
        button.addEventListener('click', () => {
          const current = Math.max(0, Number(input.value) || 0);
          const delta = Number(button.dataset.pcQuantityChange) || 0;
          const next = Math.max(0, current + delta);

          input.value = next;
          changeLine(lineElement, next, message);
        });
      });

      input?.addEventListener('change', () => {
        const quantity = Math.max(0, Number(input.value) || 0);
        input.value = quantity;
        changeLine(lineElement, quantity, message);
      });

      lineElement.querySelector('[data-pc-remove]')?.addEventListener('click', () => {
        changeLine(lineElement, 0, message);
      });
    });
  }

  function initAll() {
    document.querySelectorAll('[data-pc-cart]').forEach(initCart);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initCart(event.target.querySelector('[data-pc-cart]'));
  });
})();
